import { yupResolver } from '@hookform/resolvers/yup'
import { Loader2, Save } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { PageHeader } from '@/components/common/PageHeader'
import { Button } from '@/components/ui/button'
import { confirmModal } from '@/components/ui/modal'
import { Tabs, TabsContent } from '@/components/ui/tabs'
import { useAuth } from '@/lib/auth/AuthProvider'
import { HrSurface } from './components/HrSurface'
import { useEmployeeDetail, useSavePersonel } from './api'
import { profileToFormValues } from './model'
import { BelgeFormTab } from './form/BelgeFormTab'
import { FormHeaderCard } from './form/FormHeaderCard'
import { FORM_TABS, firstTabWithError, type FormTab } from './form/formTabs'
import { IletisimFormTab } from './form/IletisimFormTab'
import { IsFormTab } from './form/IsFormTab'
import { KisiselFormTab } from './form/KisiselFormTab'
import { PanelFormTab } from './form/PanelFormTab'
import { createPersonelFormSchema, PERSONEL_FORM_DEFAULTS, type PersonelFormMode, type PersonelFormValues } from './schemas/personelFormSchema'
import { apiErrorMessage } from '@/i18n'

const isFormTab = (v: string | null): v is FormTab => FORM_TABS.some((x) => x.value === v)

export function PersonelFormPage({ mode }: { mode: PersonelFormMode }) {
  const { id = '' } = useParams()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { can } = useAuth()
  const [params, setParams] = useSearchParams()
  const editing = mode === 'edit'
  const query = useEmployeeDetail(editing ? id : '')
  const existing = query.personel
  const current = query.profile
  const save = useSavePersonel()

  const hadPanel = Boolean(current?.panel_erisim)
  const schema = useMemo(() => createPersonelFormSchema(t, mode), [t, mode])
  const form = useForm<PersonelFormValues>({ resolver: yupResolver(schema), defaultValues: PERSONEL_FORM_DEFAULTS })

  useEffect(() => {
    if (!editing) {
      form.reset(PERSONEL_FORM_DEFAULTS)
      return
    }
    if (current && !form.formState.isDirty) form.reset(profileToFormValues(current))
  }, [editing, current, form])

  if (editing && query.isLoading) {
    return (
      <HrSurface>
        <div className="py-20 text-center text-sm text-muted-foreground">{t('common.loading')}</div>
      </HrSurface>
    )
  }
  if (editing && !existing) return <Navigate to="/hr/personel" replace />

  const raw = params.get('sekme')
  const tab: FormTab = isFormTab(raw) ? raw : 'kisisel'
  const setTab = (v: string) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (v === 'kisisel') next.delete('sekme')
        else next.set('sekme', v)
        return next
      },
      { replace: true },
    )
  const backTo = editing && existing ? `/hr/personel/${existing.id}` : '/hr/personel'
  const leave = async () => {
    if (form.formState.isDirty) {
      const ok = await confirmModal({
        title: t('hr.personel.form.unsavedTitle'),
        description: t('hr.personel.form.unsavedBody'),
        confirmLabel: t('hr.personel.form.leave'),
        destructive: true,
      })
      if (!ok) return
    }
    navigate(backTo)
  }

  const submit = form.handleSubmit(
    (values) => {
      if (editing && !hadPanel && values.panel_erisim && !(values.role_ids ?? []).length) {
        form.setError('role_ids', { message: t('hr.personel.form.rolesRequired') })
        setTab('panel')
        toast.error(t('hr.personel.form.formErrors'))
        return
      }
      save.mutate(
        { id: editing ? existing?.id : undefined, values, current, photo: values.foto_dosya ?? null },
        {
          onSuccess: (employeeId) => {
            toast.success(editing ? t('common.saved') : t('hr.personel.form.addSuccess'))
            navigate(`/hr/personel/${employeeId}`)
          },
          onError: (error) => toast.error(apiErrorMessage(error)),
        },
      )
    },
    (errors) => {
      const first = firstTabWithError(errors)
      if (first) setTab(first)
      toast.error(t('hr.personel.form.formErrors'))
    },
  )

  const canResetPassword = can('identity:user:write') || can('hr.core:employee:write') || can('admin:root')

  return (
    <HrSurface>
      <PageHeader
        backTo={backTo}
        onBack={() => void leave()}
        eyebrow={
          <span className="flex items-center gap-2">
            <Link to="/hr/personel" className="hover:text-foreground">
              {t('hr.personel.backToList')}
            </Link>
            {editing && existing ? (
              <>
                <span className="opacity-50">/</span>
                <Link to={`/hr/personel/${existing.id}`} className="hover:text-foreground">
                  {existing.ad}
                </Link>
              </>
            ) : null}
          </span>
        }
        title={editing ? t('hr.personel.form.editTitle') : t('hr.personel.form.addTitle')}
        description={editing ? t('hr.personel.form.editHint') : t('hr.personel.form.addPageHint')}
      />

      <FormProvider {...form}>
        <form noValidate onSubmit={submit}>
          <Tabs value={tab} onValueChange={setTab} className="space-y-4">
            <FormHeaderCard mode={mode} tab={tab} />

            <TabsContent value="kisisel" forceMount className="mt-0 data-[state=inactive]:hidden">
              <KisiselFormTab />
            </TabsContent>
            <TabsContent value="is" forceMount className="mt-0 data-[state=inactive]:hidden">
              <IsFormTab />
            </TabsContent>
            <TabsContent value="iletisim" forceMount className="mt-0 data-[state=inactive]:hidden">
              <IletisimFormTab />
            </TabsContent>
            <TabsContent value="belgeler" forceMount className="mt-0 data-[state=inactive]:hidden">
              <BelgeFormTab mode={mode} />
            </TabsContent>
            <TabsContent value="panel" forceMount className="mt-0 data-[state=inactive]:hidden">
              <PanelFormTab mode={mode} canResetPassword={canResetPassword} hadPanel={hadPanel} />
            </TabsContent>

            <div className="sticky bottom-0 z-10 flex justify-end rounded-2xl border border-border/70 bg-card/95 p-3 shadow-sm backdrop-blur max-sm:pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:p-4">
              <Button type="submit" className="h-10 w-full rounded-xl sm:w-auto sm:min-w-40" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? <Loader2 className="animate-spin" /> : <Save />}
                {editing ? t('common.save') : t('hr.personel.add')}
              </Button>
            </div>
          </Tabs>
        </form>
      </FormProvider>
    </HrSurface>
  )
}

export function PersonelCreatePage() {
  return <PersonelFormPage mode="create" />
}

export function PersonelEditPage() {
  return <PersonelFormPage mode="edit" />
}
